import { useEffect, useState } from "react";
import QRCode from "qrcode";

import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";

interface QrCodeProps {
  /** Raw payload emitted by PairAccountUpdate.qr_code. */
  value: string;
  size?: number;
  className?: string;
}

/**
 * Renders the pairing payload locally. The `qrcode` package is bundled; the app
 * never talks to an external QR rendering service, because the payload is a
 * short-lived WhatsApp pairing credential and must not leave the machine.
 */
export function QrCode({ value, size = 240, className }: QrCodeProps) {
  const [svg, setSvg] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    setError(undefined);

    QRCode.toString(value, {
      type: "svg",
      errorCorrectionLevel: "M",
      margin: 1,
      width: size,
      // Fixed black-on-white: WhatsApp scanners are unreliable on tinted codes,
      // so the code keeps its own quiet zone regardless of the app theme.
      color: { dark: "#000000", light: "#ffffff" },
    })
      .then((out) => {
        if (!cancelled) setSvg(out);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Could not render QR code");
      });

    return () => {
      cancelled = true;
    };
  }, [value, size]);

  if (error) {
    return (
      <div
        className="flex items-center justify-center rounded-lg border border-dashed p-4 text-center text-xs text-destructive"
        style={{ width: size, height: size }}
      >
        {error}
      </div>
    );
  }

  if (!svg) {
    return <Skeleton className={cn("rounded-lg", className)} style={{ width: size, height: size }} />;
  }

  return (
    <div
      role="img"
      aria-label="WhatsApp pairing QR code"
      className={cn("rounded-lg bg-white p-2 [&>svg]:block [&>svg]:size-full", className)}
      style={{ width: size, height: size }}
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}
